import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
   $createParagraphNode,
   $createRangeSelection,
   $getSelection,
   $insertNodes,
   $isNodeSelection,
   $isRootOrShadowRoot,
   $setSelection,
   COMMAND_PRIORITY_EDITOR,
   createCommand,
   LexicalCommand,
   LexicalEditor,
   DRAGOVER_COMMAND,
   DRAGSTART_COMMAND,
   DROP_COMMAND,
   COMMAND_PRIORITY_HIGH,
   COMMAND_PRIORITY_LOW,
   ElementNode,
} from "lexical";
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import { useEffect } from "react";
import { $createImageNode, $isImageNode, GapImageNode, GapImagePayLoad } from "../../nodes/GapImageNode";
import useStore from "reusable/mobx-store-utils";
import Loader from "reusable/mobx-helpers/Loader";
import Box from "@mui/material/Box";
import { observer } from "mobx-react-lite";
import { CAN_USE_DOM } from "../../shared/canUseDOM";
import { getUploadUrl } from "reusable/utils/file-upload";
import FileUploadWithProgress from "reusable/mui/file-uploads/FileUploadWithProgress";

export const INSERT_GAPIMAGE_COMMAND: LexicalCommand<InsertGapImagePayload> = createCommand('INSERT_GAPIMAGE_COMMAND');
export type InsertGapImagePayload = Readonly<GapImagePayLoad>;
const getDOMSelection = (targetWindow: Window | null): Selection | null =>
   CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export default function GapImagePlugin(): JSX.Element | null {
   const [editor] = useLexicalComposerContext();

   useEffect(() => {
      if (!editor.hasNodes([GapImageNode])) {
         throw new Error('GapImagePlugin: GapImageNode not registered for editor in GapNodes.ts');
      }

      return mergeRegister(
         editor.registerCommand<InsertGapImagePayload>(
            INSERT_GAPIMAGE_COMMAND,
            (payload) => {
               const imageNode = $createImageNode(payload);
               $insertNodes([imageNode]);
               if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
                  const paragraphNode = $wrapNodeInElement(imageNode, $createParagraphNode) as ElementNode;
                  paragraphNode.selectEnd();
               }

               return true;
            },
            COMMAND_PRIORITY_EDITOR,
         ),
         editor.registerCommand<DragEvent>(
            DRAGSTART_COMMAND,
            (event) => {
               return onDragStart(event);
            },
            COMMAND_PRIORITY_HIGH,
         ),
         editor.registerCommand<DragEvent>(
            DRAGOVER_COMMAND,
            (event) => {
               return onDragover(event);
            },
            COMMAND_PRIORITY_LOW,
         ),
         editor.registerCommand<DragEvent>(
            DROP_COMMAND,
            (event) => {
               return onDrop(event, editor);
            },
            COMMAND_PRIORITY_HIGH,
         ),
      );
   }, [editor]);

   return null;
}

export const InsertGapImageDialog = observer(function InsertGapImageDialog({
   activeEditor,
   onClose,
   orgId
}: {
   activeEditor: LexicalEditor
   onClose: () => void
   orgId: string
}): JSX.Element {
   const localStore = useStore(() => ({
      loader: new Loader(false),
      setFiles: async (files: File[]) => {
         const file = files[0]

         const fileDetails = {
            orgId,
            fileName: file.name
         }

         activeEditor.dispatchCommand(INSERT_GAPIMAGE_COMMAND, { src: JSON.stringify(fileDetails), altText: file.name })
         onClose()
      },
      getPresignedUrl: (file: File) => getUploadUrl({ orgId, type: 'RichTextEditor' }, file),
   }))

   return (
      <Box sx={{ width: '600px', maxWidth: '100%' }}>
         <FileUploadWithProgress
            files={[]}
            fileOrgId={orgId}
            getPresignedUrl={localStore.getPresignedUrl}
            onFilesValidated={localStore.setFiles}
            accept={{ "image/png": ['.png'], "image/jpeg": ['.jpeg', '.jpg'] }}
            appendUlids
         />
      </Box>
   )
})

const TRANSPARENT_IMAGE =
   'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function onDragStart(event: DragEvent): boolean {
   const node = getImageNodeInSelection();
   if (!node) {
      return false;
   }
   const dataTransfer = event.dataTransfer;
   if (!dataTransfer) {
      return false;
   }
   dataTransfer.setData('text/plain', '_');
   dataTransfer.setDragImage(img, 0, 0);
   dataTransfer.setData(
      'application/x-lexical-drag',
      JSON.stringify({
         data: {
            altText: node.__altText,
            caption: node.__caption,
            height: node.__height,
            key: node.getKey(),
            maxWidth: node.__maxWidth,
            showCaption: node.__showCaption,
            src: node.__src,
            width: node.__width,
         },
         type: 'image',
      }),
   );

   return true;
}

function onDragover(event: DragEvent): boolean {
   const node = getImageNodeInSelection();
   if (!node) {
      return false;
   }
   if (!canDropImage(event)) {
      event.preventDefault();
   }
   return true;
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
   const node = getImageNodeInSelection();
   if (!node) {
      return false;
   }
   const data = getDragImageData(event);
   if (!data) {
      return false;
   }
   event.preventDefault();
   if (canDropImage(event)) {
      const range = getDragSelection(event);
      node.remove();
      const rangeSelection = $createRangeSelection();
      if (range !== null && range !== undefined) {
         rangeSelection.applyDOMRange(range);
      }
      $setSelection(rangeSelection);
      editor.dispatchCommand(INSERT_GAPIMAGE_COMMAND, data);
   }
   return true;
}

function getImageNodeInSelection(): GapImageNode | null {
   const selection = $getSelection();
   if (!$isNodeSelection(selection)) {
      return null;
   }
   const nodes = selection.getNodes();
   const node = nodes[0];
   return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertGapImagePayload {
   const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
   if (!dragData) {
      return null;
   }
   const { type, data } = JSON.parse(dragData);
   if (type !== 'image') {
      return null;
   }

   return data;
}

declare global {
   interface DragEvent {
      rangeOffset?: number;
      rangeParent?: Node;
   }
}

function canDropImage(event: DragEvent): boolean {
   const target = event.target;
   return !!(
      target &&
      target instanceof HTMLElement &&
      !target.closest('code, span.editor-image') &&
      target.parentElement &&
      target.parentElement.closest('div.ContentEditable__root')
   );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
   let range;
   const target = event.target as null | Element | Document;
   const targetWindow =
      target == null
         ? null
         : target.nodeType === 9
            ? (target as Document).defaultView
            : (target as Element).ownerDocument.defaultView;
   const domSelection = getDOMSelection(targetWindow);
   if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(event.clientX, event.clientY);
   } else if (event.rangeParent && domSelection !== null) {
      domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
      range = domSelection.getRangeAt(0);
   } else {
      throw Error(`Cannot get the selection when dragging`);
   }

   return range;
}
